import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { Pc } from "@/types/pc";
import type { CurrentUser } from "@/types/user";
import type { Reservation } from "@/types/reservation";
import { useReservations } from "@/hooks/useReservations";
import { LoadingState } from "@/components/LoadingState";
import { EmptyState } from "@/components/EmptyState";
import { ReservationCard } from "@/components/ReservationCard";
import { ReservationModal } from "@/components/ReservationModal";
import { RETURNABLE_STATUSES } from "@/lib/status";
import { api } from "@/lib/apiClient";

export function MyReservationsPage({ currentUser }: { currentUser: CurrentUser }) {
  const { reservations, loading, error, reload } = useReservations({ mine: true });
  const [pcs, setPcs] = useState<Pc[]>([]);
  const [selected, setSelected] = useState<Reservation | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const returnableOnly = searchParams.get("filter") === "returnable";

  useEffect(() => {
    api.get<Pc[]>("/pcs").then(setPcs).catch(() => undefined);
  }, []);

  const pcName = useMemo(() => {
    const map = new Map(pcs.map((p) => [p.id, p.name]));
    return (id: number) => map.get(id) ?? `PC#${id}`;
  }, [pcs]);

  const visible = returnableOnly
    ? reservations.filter((r) => RETURNABLE_STATUSES.includes(r.status))
    : reservations;

  const sorted = [...visible].sort((a, b) => new Date(b.start_at).getTime() - new Date(a.start_at).getTime());

  return (
    <div>
      <h1 className="page-title">自分の利用</h1>
      <p className="page-subtitle">自分の申請・利用履歴を確認できます。</p>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button
          className={!returnableOnly ? "btn btn-primary" : "btn btn-secondary"}
          style={{ height: 36, padding: "0 14px" }}
          onClick={() => setSearchParams({})}
        >
          すべて
        </button>
        <button
          className={returnableOnly ? "btn btn-primary" : "btn btn-secondary"}
          style={{ height: 36, padding: "0 14px" }}
          onClick={() => setSearchParams({ filter: "returnable" })}
        >
          返却待ちのみ
        </button>
      </div>

      {loading ? (
        <LoadingState />
      ) : error ? (
        <EmptyState message={error} />
      ) : sorted.length === 0 ? (
        <EmptyState message="該当する利用がありません。" />
      ) : (
        <div>
          {sorted.map((r) => (
            <ReservationCard
              key={r.id}
              reservation={r}
              pcName={pcName(r.pc_id)}
              userName={currentUser.displayName}
              onClick={() => setSelected(r)}
            />
          ))}
        </div>
      )}

      {selected && (
        <ReservationModal
          reservation={selected}
          pcName={pcName(selected.pc_id)}
          userName={currentUser.displayName}
          currentUser={currentUser}
          onClose={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            reload();
          }}
        />
      )}
    </div>
  );
}
